/** Escape for inserting user/catalog text into print HTML. */
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export type IsbnSpineLabelPayload = {
  title: string;
  callNumber: string;
  barcodes: string[];
};

const LABEL_PRINT_HTML = (title: string, callNumber: string, barcodes: string[]) => {
  const blocks = barcodes
    .map(
      (barcode) => `
    <div class="label">
      <div class="t">${escapeHtml(title)}</div>
      <div class="cn">${escapeHtml(callNumber || "—")}</div>
      <div class="bc">${escapeHtml(barcode)}</div>
    </div>`
    )
    .join("");

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Spine / item labels</title>
  <style>
    * { box-sizing: border-box; }
    body { font-family: system-ui, sans-serif; margin: 0; padding: 12px; color: #111; }
    @page { margin: 0.35in; }
    @media print {
      .label { break-inside: avoid; page-break-inside: avoid; }
    }
    .label {
      border: 1px solid #ccc; border-radius: 6px; padding: 10px 12px; margin-bottom: 12px;
      max-width: 4.25in; min-height: 1.1in;
    }
    .t { font-weight: 600; font-size: 13px; line-height:1.3; }
    .cn { font-size: 11px; color: #444; margin-top: 4px; }
    .bc { font-family: ui-monospace, monospace; font-size: 13px; margin-top: 6px; letter-spacing: 0.02em; }
  </style>
</head>
<body>${blocks}
</body>
</html>`;
};

/**
 * Opens a minimal document with one label per barcode and triggers the browser print dialog.
 * Uses a blob: URL (reliable) instead of <code>document.write</code> on a bare window, which can stay on about:blank.
 */
export function openIsbnSpineLabelPrint({ title, callNumber, barcodes }: IsbnSpineLabelPayload) {
  if (barcodes.length === 0) {
    return false;
  }
  const html = LABEL_PRINT_HTML(title, callNumber, barcodes);
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const w = window.open(url, "_blank", "width=640,height=720");
  if (!w) {
    URL.revokeObjectURL(url);
    return false;
  }

  const printAndCleanup = () => {
    try {
      w.focus();
      w.print();
    } finally {
      setTimeout(() => URL.revokeObjectURL(url), 120_000);
    }
  };

  if (w.document?.readyState === "complete") {
    setTimeout(printAndCleanup, 150);
  } else {
    w.addEventListener("load", () => setTimeout(printAndCleanup, 100), { once: true });
  }

  return true;
}
